import createChangedItemEntry from '../helpers/createChangedItemEntry.js'
import isItemChanged from '../helpers/isItemChanged.js'
import isEmpty from '../helpers/isEmpty.js'
import store from '../store/index'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function normaliseRaceValue(raw) {
  if (raw === null || raw === undefined || raw === '') return null
  if (typeof raw === 'object') return raw.id ?? null
  if (typeof raw !== 'string') return raw

  const trimmed = raw.trim()
  if (!trimmed) return null
  if (['unknown', 'n/a', 'na', 'none'].includes(trimmed.toLowerCase())) return null
  if (UUID_RE.test(trimmed)) return trimmed

  const race = store.getters?.['races/getRaceByName']?.(trimmed)
  return race?.id ?? null
}

function normaliseCurrencyValue(raw) {
  if (raw === null || raw === undefined || raw === '') return null
  if (typeof raw === 'object') return raw.id ?? null
  if (typeof raw !== 'string') return raw

  const trimmed = raw.trim()
  if (!trimmed) return null
  if (['unknown', 'n/a', 'na', 'none'].includes(trimmed.toLowerCase())) return null
  if (UUID_RE.test(trimmed)) return trimmed

  const currencies = store.getters?.['characters/getCurrencies'] ?? []
  const match = currencies.find(
    (currency) =>
      currency?.id === trimmed || currency?.name === trimmed || currency?.short_name === trimmed,
  )

  return match?.id ?? null
}

function stripArtefactOnlyPayloadFields(entry, itemType) {
  if (!entry || entry.action !== 'UPDATE' || itemType === 'Artefact') {
    return
  }

  if (!entry.payload || typeof entry.payload !== 'object') {
    return
  }

  // ENC is always read-only from the static Item definition
  // Never allow character-item UPDATE payloads to include enc
  if (Object.prototype.hasOwnProperty.call(entry.payload, 'enc')) {
    delete entry.payload.enc
  }

  const artefactOnlyKeys = [
    'artefact_type',
    'artefact_base_item',
    'material',
    'quality',
    'age',
    'race',
    'currency',
    'value_lower',
    'value_upper',
    'estimated_value',
  ]

  artefactOnlyKeys.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(entry.payload, key)) {
      delete entry.payload[key]
    }
  })

  if (Array.isArray(entry.payload.runes) && entry.payload.runes.length === 0) {
    delete entry.payload.runes
  }

  if (
    (Array.isArray(entry.payload.spells) && entry.payload.spells.length === 0) ||
    (Array.isArray(entry.payload.non_rune_spells) && entry.payload.non_rune_spells.length === 0)
  ) {
    delete entry.payload.spells
    delete entry.payload.non_rune_spells
  }
}

//--------------------
// track item changes
//--------------------
export default function trackItemChanges({
  change = null,
  item,
  original_items,
  changed_items,
  character_id,
  delete_payload = null,
  delete: del = false,
} = {}) {
  console.log('===================================================')
  console.log('IN trackItemChanges')
  console.log('item name = ', item && item.name)
  console.log('item = ', window.$deepCopy(item))
  console.log('change = ', window.$deepCopy(change))
  console.log('original_items = ', window.$deepCopy(original_items))
  console.log('changed_items = ', window.$deepCopy(changed_items))
  console.log('----------------------------------')

  //---------------------
  // check args received
  //---------------------
  if (isEmpty(item) || original_items === undefined || changed_items === undefined) {
    return []
  }

  //----------------------------------------------
  // static artefact fields (on underlying Item)
  //----------------------------------------------
  const staticArtefactKeys = [
    'name',
    'artefact_type',
    'artefact_base_item',
    'trait',
    'material',
    'quality',
    'age',
    'value_lower',
    'value_upper',
    'race',
    'currency',
    'description',
    'short_description',
    'enc',
    'ap',
    'hp',
    'pwr_storage',
    'estimated_value',
    // countable artefact fields that live on the static Item
    'countable',
    'unit_name',
    'max_unit_count',
    'healing_type',
    'recovers',
    'poison_type',
    'pot',
    'effect',
    'delay',
    'damage_dice',
    'duration',
    'resist',
    'image_file',
  ]

  const isTempStaticItemId = typeof item?.item === 'string' && item.item.startsWith('TMP-')

  const needsArtefactStaticCreate =
    item?.type === 'Artefact' && (item?.create_static_item === true || isTempStaticItemId)

  const buildInitialArtefactStaticUpdates = (sourceItem) => {
    if (sourceItem?.type !== 'Artefact' || !sourceItem?.item) {
      return null
    }

    const baseline = sourceItem._initial_static_values ?? null
    if (!baseline) {
      return null
    }

    const resolvedCurrency =
      sourceItem.currency === null || sourceItem.currency === undefined
        ? (baseline.currency ?? null)
        : normaliseCurrencyValue(sourceItem.currency)

    const candidateUpdates = {
      artefact_type: sourceItem.artefact_type ?? '',
      artefact_base_item: sourceItem.artefact_base_item ?? null,
      trait: sourceItem.trait ?? null,
      material: sourceItem.material ?? '',
      quality: sourceItem.quality ?? '',
      age: sourceItem.age ?? '',
      race: normaliseRaceValue(sourceItem.race),
      currency: resolvedCurrency,
      value_lower: sourceItem.value_lower ?? 0,
      value_upper: sourceItem.value_upper ?? 0,
      estimated_value: sourceItem.estimated_value ?? false,
      description: sourceItem.description ?? '',
      short_description: sourceItem.short_description ?? '',
      enc: sourceItem.enc ?? 0,
      ap: sourceItem.ap ?? 0,
      hp: sourceItem.hp ?? 0,
      pwr_storage: sourceItem.pwr_storage ?? 0,
      countable: sourceItem.countable ?? false,
      unit_name: sourceItem.unit_name ?? '',
      max_unit_count: sourceItem.max_unit_count ?? 0,
      healing_type: sourceItem.healing_type ?? null,
      recovers: sourceItem.recovers ?? null,
      poison_type: sourceItem.poison_type ?? null,
      pot: sourceItem.pot ?? null,
      effect: sourceItem.effect ?? null,
      delay: sourceItem.delay ?? null,
      damage_dice: sourceItem.damage_dice ?? null,
      duration: sourceItem.duration ?? null,
      resist: sourceItem.resist ?? null,
    }

    const hasConcreteImageFile =
      Object.prototype.hasOwnProperty.call(sourceItem, 'image_file') &&
      sourceItem.image_file !== null &&
      sourceItem.image_file !== undefined

    if (hasConcreteImageFile || sourceItem?.image_cleared === true) {
      candidateUpdates.image_file = sourceItem.image_file ?? null
    }

    const staticUpdates = Object.fromEntries(
      Object.entries(candidateUpdates).filter(([key, value]) => {
        const baselineValue = baseline[key]
        if (baselineValue == null && value == null) {
          return false
        }
        return baselineValue !== value
      }),
    )

    return Object.keys(staticUpdates).length > 0 ? staticUpdates : null
  }

  /*
  -------------------------------------------------------------------------------------------------------------------------
   CHANGED ARRAY    ORIGINAL ARRAY     PARM ACTION         CHANGED ARRAY ACTION
  ---------------  ----------------   ------------         ---------------------
  A.    No               No               ADD              add (new character item)
  B.    No               Yes              UPDATE           add existing character item update
  C.    Yes              Yes              UPDATE/REMOVE    further character item update or reverting to original)
  D.    Yes              No               ADD              update same ADD entry (further updates having flagged addition)
  -------------------------------------------------------------------------------------------------------------------------
  */

  let item_changed_parm = {}

  //---------------------------------------------------------------------
  // find item in change array (using id if existing item - or temp id)
  //---------------------------------------------------------------------
  const changed_idx = changed_items.findIndex((c) => (item.id != null ? c.id === item.id : false))
  console.log('XXXXX changed_idx = ', changed_idx)

  //--------------------------------------------------------------------
  // find item in original array (wont be an original entry without id)
  //--------------------------------------------------------------------
  const original_idx = original_items.findIndex((o) => o.id === item.id)

  //=========================================================================================================================
  // A. where item NOT in change array + NOT in original array (adding new character item)
  //---------------------------------------------------------------------------------------
  if (changed_idx === -1 && original_idx === -1) {
    console.log(
      'A. ITEM NOT IN CHANGED OR ORIGINAL ARRAY NAME = ',
      item.name,
      ' display_name = ',
      item.display_name,
    )

    //-------------------------------------------------------
    // static item id passed from item addition via ItemCard
    //-------------------------------------------------------
    if (needsArtefactStaticCreate) {
      console.log(
        `DEBUG trackItemChanges ARTEFACT CREATE_STATIC: item.name=${item.name}, ap=${item.ap}, hp=${item.hp}, item.item=${item.item}`,
      )
      item_changed_parm = {
        name: item.name,
        type: item.type,
        action: 'ADD',
        id: item.id,
        payload: {
          artefact_mode: 'CREATE_STATIC',
          character: character_id ? character_id : null,
          item: item.item ? item.item : null,
          item_name: item.name ?? '',
          item_type: item.type ?? 'Artefact',
          artefact_type: item.artefact_type ?? '',
          artefact_base_item: item.artefact_base_item ?? null,
          trait: item.trait ?? null,
          material: item.material ?? '',
          quality: item.quality ?? '',
          age: item.age ?? '',
          race: item.race ?? null,
          currency: item.currency ?? null,
          value_lower: item.value_lower ?? 0,
          value_upper: item.value_upper ?? 0,
          estimated_value: item.estimated_value ?? false,
          description: item.description ?? '',
          image_file: item.image_file ?? null,
          enc: item.enc ?? 0,
          tally_qty: item.tally_qty ?? 0,
          player_notes: item.player_notes ?? item.notes ?? '',
          short_description: item.short_description ?? '',
          worn_location: item.worn_location ?? null,
          container: item.container ?? null,
          attach_location: item.attach_location ?? null,
          armour_location: item.armour_location ?? null,
          ap: item.ap ?? 0,
          hp: item.hp ?? 0,
          held: item.held ?? false,
          equipped: item.equipped ?? false,
          runes: item.runes ?? [],
          non_rune_spells: item.non_rune_spells ?? [],
          tallyable: item.tallyable ?? false,
          storeable: item.storeable ?? true,
          countable: item.countable ?? false,
          unit_name: item.unit_name ?? '',
          max_unit_count: item.max_unit_count ?? 0,
          cur_unit_count: item.cur_unit_count ?? 0,
          healing_type: item.healing_type ?? null,
          recovers: item.recovers ?? null,
          poison_type: item.poison_type ?? null,
          pot: item.pot ?? null,
          effect: item.effect ?? null,
          delay: item.delay ?? null,
          damage_dice: item.damage_dice ?? null,
          duration: item.duration ?? null,
          resist: item.resist ?? null,
          pwr_storage: item.pwr_storage ?? 0,
        },
      }

      if (item.display_name) {
        item_changed_parm.payload.display_name = item.display_name
      }
    } else {
      console.log(
        `DEBUG trackItemChanges STANDARD ITEM ADD: item.name=${item.name}, ap=${item.ap}, hp=${item.hp}, type=${item.type}`,
      )

      const isCountable = item.countable === true
      const resolvedMaxUnitCount = item.max_unit_count ?? null
      const resolvedCurUnitCount =
        isCountable && (item.cur_unit_count === null || item.cur_unit_count === undefined)
          ? resolvedMaxUnitCount
          : (item.cur_unit_count ?? null)

      item_changed_parm = createChangedItemEntry({
        name: item.name,
        type: item.type,
        display_name: item.display_name ? item.display_name : '',
        id: item.id, // real or temp id used for tracking
        action: 'ADD',
        payload: {
          character: character_id ? character_id : null,
          item: item.item ? item.item : null, // static item id
          tally_qty: item.tally_qty,
          player_notes: item.player_notes ?? item.notes,
          short_description: item.short_description ?? '',
          // ENC is read-only from static Item definition - never send from client
          worn_location: item.worn_location,
          container: item.container,
          attach_location: item.attach_location,
          armour_location: item.armour_location,
          ap: item.ap ?? 0,
          hp: item.hp,
          pwr_storage: item.pwr_storage,
          held: item.held,
          equipped: item.equipped,
          runes: item.runes,
          non_rune_spells: item.non_rune_spells,
          unit_name: item.unit_name ?? null,
          max_unit_count: resolvedMaxUnitCount,
          cur_unit_count: resolvedCurUnitCount,
        },
      })

      if (item.type === 'Artefact' && item.item != null) {
        item_changed_parm.item_id = item.item
        item_changed_parm.static_updates = buildInitialArtefactStaticUpdates(item) ?? undefined
      }
    }
    console.log(
      `DEBUG trackItemChanges PUSHING ITEM: name=${item_changed_parm.name}, action=${item_changed_parm.action}, payload=${JSON.stringify(item_changed_parm.payload)}`,
    )
    changed_items.push(item_changed_parm)

    //=========================================================================================================================
    // B. where item NOT in change array + IS in original array (deleting or updating existing character item)
    //-----------------------------------------------------------------------------------------------------------
  } else if (changed_idx === -1 && original_idx !== -1) {
    console.log('B. ITEM NOT IN CHANGED ARRAY + IS IN ORIGINAL ARRAY: ' + item.name)

    //--------------------------------
    // where deleting character item
    //--------------------------------
    if (del) {
      console.log('B1. CREATE DELETION ENTRY')
      item_changed_parm = createChangedItemEntry({
        name: item.name,
        action: 'DELETE',
        id: item.id, // character_item id or temp id
        payload: delete_payload ?? undefined,
      })
    } else {
      console.log('B2. CREATE UPDATE ENTRY')

      const isArtefactStaticField =
        item.type === 'Artefact' && staticArtefactKeys.includes(change.key)
      const shouldMirrorStaticArtefactFieldToCharacterItem =
        item.type === 'Artefact' &&
        ['pwr_storage', 'ap', 'hp', 'max_unit_count'].includes(change.key)
      const isArtefactImageFile = item.type === 'Artefact' && change.key === 'image_file'

      item_changed_parm = {
        name: item.name,
        type: item.type,
        action: 'UPDATE',
        id: item.id,
        payload: {},
      }

      if (isArtefactStaticField) {
        item_changed_parm.item_id = item.item != null ? item.item : null
        item_changed_parm.static_updates = { [change.key]: change.value }

        if (shouldMirrorStaticArtefactFieldToCharacterItem) {
          item_changed_parm.payload[change.key] = change.value
        }

        // When an artefact is made non-countable, also clear related
        // static count fields on the underlying Item. Use an empty
        // string for unit_name because the model field does not allow
        // null (blank string is the "no unit" state).
        if (change.key === 'countable' && change.value === false) {
          item_changed_parm.static_updates.unit_name = ''
          item_changed_parm.static_updates.max_unit_count = 0
          item_changed_parm.payload.max_unit_count = 0
          item_changed_parm.payload.cur_unit_count = 0
        }
      } else if (isArtefactImageFile) {
        // Route artefact image uploads to the static Item endpoint.
        // change.value is a FormData instance with an "image" field.
        item_changed_parm.item_id = item.item != null ? item.item : null
        item_changed_parm.image_formdata = change.value
      } else {
        item_changed_parm.payload[change.key] = change.value
      }
    }
    changed_items.push(item_changed_parm)

    //=========================================================================================================================
    // C. where item IS in change array + IS in original array (updating existing change entry/remove if reverted)
    //--------------------------------------------------------------------------------------------------------------
  } else if (changed_idx !== -1 && original_idx !== -1) {
    console.log('C. ITEM IS IN CHANGED ARRAY + IS IN ORIGINAL ARRAY: ' + item.name)
    console.log('C1. CHECK IF ITEM CHANGED')

    if (del) {
      console.log('C2. CREATE DELETION ENTRY')
      changed_items.splice(changed_idx, 1)
      item_changed_parm = createChangedItemEntry({
        name: item.name,
        action: 'DELETE',
        id: item.id,
        payload: delete_payload ?? undefined,
      })
      changed_items.push(item_changed_parm)
    } else {
      const item_changed = isItemChanged(original_items[original_idx], item)

      if (item_changed) {
        console.log('C3. ITEM VAL CHANGED - UPDATE ENTRY: ' + item.name)

        const isArtefactStaticField =
          item.type === 'Artefact' && staticArtefactKeys.includes(change.key)
        const shouldMirrorStaticArtefactFieldToCharacterItem =
          item.type === 'Artefact' &&
          ['pwr_storage', 'ap', 'hp', 'max_unit_count'].includes(change.key)
        const isArtefactImageFile = item.type === 'Artefact' && change.key === 'image_file'

        const entry = changed_items[changed_idx]

        if (!entry.type) {
          entry.type = item.type
        }

        if (isArtefactStaticField) {
          if (!entry.static_updates) {
            entry.static_updates = {}
          }
          entry.static_updates[change.key] = change.value

          if (shouldMirrorStaticArtefactFieldToCharacterItem) {
            entry.payload[change.key] = change.value
          }

          if (change.key === 'countable' && change.value === false) {
            entry.static_updates.unit_name = ''
            entry.static_updates.max_unit_count = 0
            entry.payload.max_unit_count = 0
            entry.payload.cur_unit_count = 0
          }

          if (entry.item_id == null) {
            entry.item_id = item.item != null ? item.item : null
          }
        } else if (isArtefactImageFile) {
          // Store FormData for artefact image upload separately so
          // the static Item endpoint can be called with multipart.
          entry.item_id = entry.item_id ?? (item.item != null ? item.item : null)
          entry.image_formdata = change.value
        } else {
          entry.payload[change.key] = change.value
        }
      } else {
        console.log('C4. ITEM NOT CHANGED - REMOVE ENTRY: ' + item.name)
        changed_items.splice(changed_idx, 1)
      }
    }

    //=========================================================================================================================
    // D. where item IS in change array + NOT in original array (updating an ADD entry - further changes before commit)
    //------------------------------------------------------------------------------------------------------------------
  } else {
    console.log('D. ITEM IS IN CHANGED ARRAY + NOT IN ORIGINAL ARRAY: ' + item.name)

    if (del) {
      console.log('D1. NEW ITEM BEING DELETED - REMOVE ENTRY: ' + item.name)
      changed_items.splice(changed_idx, 1)
    } else {
      console.log('D2. UPDATE THE UPDATE ENTRY')
      const isArtefactStaticField =
        item.type === 'Artefact' && staticArtefactKeys.includes(change.key)
      const shouldMirrorStaticArtefactFieldToCharacterItem =
        item.type === 'Artefact' &&
        ['pwr_storage', 'ap', 'hp', 'max_unit_count'].includes(change.key)
      const isArtefactImageFile = item.type === 'Artefact' && change.key === 'image_file'

      const entry = changed_items[changed_idx]
      console.log('D3. changed_items[changed_idx] = ', window.$deepCopy(entry))
      console.log('D4. change = ', change)

      if (isArtefactStaticField) {
        if (entry.payload?.artefact_mode === 'CREATE_STATIC') {
          entry.payload[change.key] = change.value

          if (change.key === 'countable' && change.value === false) {
            entry.payload.unit_name = ''
            entry.payload.max_unit_count = 0
          }
        } else {
          if (!entry.static_updates) {
            entry.static_updates = {}
          }
          entry.static_updates[change.key] = change.value

          if (shouldMirrorStaticArtefactFieldToCharacterItem) {
            entry.payload[change.key] = change.value
          }

          if (change.key === 'countable' && change.value === false) {
            entry.static_updates.unit_name = ''
            entry.static_updates.max_unit_count = 0
            entry.payload.max_unit_count = 0
            entry.payload.cur_unit_count = 0
          }

          if (entry.item_id == null) {
            entry.item_id = item.item != null ? item.item : null
          }
        }
      } else if (isArtefactImageFile) {
        // New artefact being added and then given an image before
        // save: keep multipart data on the composite-create payload.
        if (entry.payload?.artefact_mode === 'CREATE_STATIC') {
          entry.payload.image_file = change.value
        } else {
          entry.item_id = entry.item_id ?? (item.item != null ? item.item : null)
          entry.image_formdata = change.value
        }
      } else {
        entry.payload[change.key] = change.value
      }
    }
  }

  changed_items.forEach((entry) => {
    stripArtefactOnlyPayloadFields(entry, entry?.type ?? item?.type)
  })

  return changed_items
}
